// File: src/App.js 
// ooosh Tours Driver Verification - PRODUCTION READY v3.7
// v3.13: ProgressTracker / ContactDetails / InsuranceQuestionnaire hoisted to
//        their own modules — nested components were remounting and losing form state
// v3.6: Added POA Instructions gateway and address mismatch flow
// - New POAInstructionsPage before POA uploads
// - addressMismatch URL parameter flows through POA1 → POA2 → passport (if needed)
// - UK drivers with address mismatch: POA1 → POA2 → Passport → DVLA
// v3.6: Add welcome back to allow returning drivers to update existing docs
// v3.5: SECURITY - Server-side session token authentication for driver-status API
// v3.4: Timeout redirect preserves URL, duplicate hire form check after email verification
// v3.3: UI refresh - standardised fonts, compact progress tracker, professional styling
// v3.2: Standardised spelling (American), added licenseEnding to driver status
// v3.1: Fixed POA provider props for bidirectional checking (returning drivers)
// v3.1.1: Changed phone number validation from 9 to 7 digits (re Norwegian phone)

import React, { useState, useEffect, useCallback } from 'react';
import ErrorBoundary from './ErrorBoundary';
import WelcomeBackPage from './WelcomeBackPage';

// Global styles
import './styles/global.css';

// Page components
import DVLAPreviewPage from './DVLAPreviewPage';
import DVACheckPage from './DVACheckPage';
import { isNiIssuer, isUkIssuer } from './utils/licenceIssuer';
import reportError from './utils/errorReporter';
import {
  StepShell, PlainShell, Card, PrimaryButton, Note, Why, Spinner, StepRow, Logo, PHASE,
  HELP_PHONE_DISPLAY, HELP_PHONE_TEL, HELP_EMAIL, TERMS_URL, firstNameOf, formatHireRange
} from './ui/HireFormUI';
import POA1Page from './POA1Page';
import POA2Page from './POA2Page';
import POAInstructionsPage from './POAInstructionsPage';  // v3.6: New gateway component
import PassportUploadPage from './PassportUploadPage';
import ProcessingHub from './ProcessingHub';
import SignaturePage from './SignaturePage';
// v3.13: these two used to be declared inside the App component below (along
// with ProgressTracker, which they now import directly). See
// InsuranceQuestionnaire.js for why that quietly threw away the driver's
// answers — do not move them back in.
import ContactDetails from './ContactDetails';
import InsuranceQuestionnaire from './InsuranceQuestionnaire';

const DriverVerificationApp = () => {
  const [jobId, setJobId] = useState('');
  const [driverEmail, setDriverEmail] = useState('');
  const [countryCode, setCountryCode] = useState('+44');
  const [phoneNumber, setPhoneNumber] = useState(''); 
  const [verificationCode, setVerificationCode] = useState('');
  const [currentStep, setCurrentStep] = useState('landing');
  const [jobDetails, setJobDetails] = useState(null);
  const [driverStatus, setDriverStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  
  // v3.0: POA data for passing between POA1 and POA2
  const [poa1Data, setPoa1Data] = useState(null);
  
  // v3.4: Already completed hire form info
  const [alreadyCompletedHireInfo, setAlreadyCompletedHireInfo] = useState(null);
  
  // v3.6: Address mismatch flag (user declared their POA address differs from licence)
  const [addressMismatch, setAddressMismatch] = useState(false);

  // 🔒 SECURITY: Validate active session
  const validateSession = (requiredEmail) => {
    const sessionToken = sessionStorage.getItem('verificationSessionToken');
    const sessionEmail = sessionStorage.getItem('sessionDriverEmail');
    const sessionStart = sessionStorage.getItem('sessionStartTime');
    
    // Check 1: Session exists?
    if (!sessionToken || !sessionEmail || !sessionStart) {
      console.log('❌ No active session - redirecting to start');
      return false;
    }
    
    // Check 2: Session matches email?
    if (sessionEmail !== requiredEmail) {
      console.log('❌ Session email mismatch - redirecting to start');
      sessionStorage.clear();
      return false;
    }
    
    // Check 3: Session not expired? (90 mins — long enough to survive a slow
    // Idenfy flow, still inside the 120-min server token TTL)
    const elapsed = Date.now() - parseInt(sessionStart);
    const sessionMaxMs = 90 * 60 * 1000;
    if (elapsed > sessionMaxMs) {
      console.log('❌ Session expired - redirecting to start');
      sessionStorage.clear();
      return false;
    }
    
    console.log('✅ Valid active session');
    return true;
  }; 

  // 🔒 v3.5 SECURITY: Get session token for authenticated API calls
  const getSessionToken = () => {
    return sessionStorage.getItem('verificationSessionToken') || '';
  };

  // ✨ v3.4 UPDATED: Session timeout check - now preserves URL and returns to email entry
  useEffect(() => {
    const checkSession = () => {
      const startTime = sessionStorage.getItem('sessionStartTime');
      if (!startTime) return;
      
      const elapsed = Date.now() - parseInt(startTime);
      const sessionMaxMs = 90 * 60 * 1000;

      if (elapsed > sessionMaxMs && currentStep !== 'already-completed') {
        console.log('⏱️ Session expired - returning to email entry (URL preserved)');
        sessionStorage.clear();
        setError('Your session has expired for security. Please enter your email again to continue.');
        // v3.4: Instead of redirecting (which loses URL), just reset to email entry
        setCurrentStep('email-entry');
        setVerificationCode('');
      }
    };
    
    // Check every minute
    const interval = setInterval(checkSession, 60000);
    return () => clearInterval(interval);
  }, [currentStep]);

  // ✨ v3.6 UPDATED: Step validation - includes poa-instructions step
  const isValidStepTransition = useCallback((requestedStep) => {
    const stepOrder = [
      'landing',
      'start',              // Oct 2026: what to have ready, before the email
      'email-entry',
      'email-verification',
      'resume',             // Oct 2026: returning driver, not finished (display only)
      'contact-details',
      'insurance-questionnaire',
      'document-upload',
      'processing-hub',
      'poa-instructions', // v3.6: New gateway step before POA uploads
      'poa1',
      'poa2',
      'dvla-check',
      'passport-upload',
      'welcome-back',
      'signature'
    ];
    
    const currentIndex = stepOrder.indexOf(currentStep);
    const requestedIndex = stepOrder.indexOf(requestedStep);
    
    // 🔒 SECURITY: Protected pages accessible via URL
    // These are validated with session checks in URL handler
    if (['processing-hub', 'poa-instructions', 'poa1', 'poa2', 'dvla-check', 'passport-upload'].includes(requestedStep)) {
      return true;
    }
    
    // 🔒 SECURITY: Signature page requires session validation
    // Session validated in URL handler before allowing access
    if (requestedStep === 'signature') {
      return true; // Session checked in URL handler
    }
    
    // v3.4: Allow transition to already-completed
    if (requestedStep === 'already-completed') {
      return true;
    }

    // A new driver goes from the code straight to their phone number; only a
    // returning driver sees the 'resume' screen in between.
    if (requestedStep === 'contact-details' && currentStep === 'email-verification') {
      return true;
    }

    // Terminal state — the driver's photo ID needs a human at Ooosh. Allowed
    // explicitly rather than relying on the "unknown step sorts backward"
    // fallthrough, which permits it only by accident.
    if (requestedStep === 'manual-review') {
      return true;
    }

    // v3.6: Allow returnign drivers to amend existing docs
    if (requestedStep === 'signature' || requestedStep === 'welcome-back') {
    return true;
}
    
    // Can go backward or forward one step at a time
    if (requestedIndex <= currentIndex) {
      return true; // Backward navigation allowed
    }
    
    if (requestedIndex === currentIndex + 1) {
      return true; // Forward one step allowed
    }
    
    // 🔒 SECURITY: Block jumping ahead
    console.warn('⚠️ Invalid step transition blocked:', currentStep, '→', requestedStep);
    return false;
  }, [currentStep]);

  // 🔒 SECURITY: Validated step transition handler
  const handleStepChange = useCallback((nextStep) => {
    if (!isValidStepTransition(nextStep)) {
      setError('Invalid navigation detected. Please follow the verification steps in order.');
      return;
    }
    
    // 🧹 CLEANUP: Minimal logging
    console.log('✅ Step transition:', currentStep, '→', nextStep);
    setCurrentStep(nextStep);
    setError(''); // Clear any previous errors
  }, [currentStep, isValidStepTransition]);
  
  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileDevice = /android|iPhone|iPad|iPod|blackberry|iemobile|opera mini/i.test(userAgent);
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isMobileDevice || isSmallScreen);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Every step starts at the top of the page. (The font is DM Sans, loaded in
  // public/index.html — the old Montserrat injection here set an inline body
  // font that would have overridden it.)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  // v3.5 UPDATED: Helper to refresh driver status (now with session token)
  const refreshDriverStatus = async (email) => {
    try {
      const response = await fetch(`/.netlify/functions/driver-status?email=${encodeURIComponent(email)}&t=${Date.now()}`, {
        headers: {
          'X-Session-Token': getSessionToken()
        }
      });
      if (response.ok) {
        const data = await response.json();
        setDriverStatus(data);
        return data;
      }
    } catch (err) {
      console.error('❌ Error refreshing driver status:', err.message);
    }
    return null;
  };

  // ==========================================================================
  // v3.7: SMART ROUTING - Verify user should actually be on the requested step
  // Calls get-next-step to check document validity dates, redirects if wrong
  // ==========================================================================
  const smartRoute = async (requestedStep, emailParam, jobParam) => {
    try {
      console.log(`🧭 Smart route check: requested=${requestedStep}`);
      
      const routerResponse = await fetch('/.netlify/functions/get-next-step', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: emailParam,
          currentStep: 'insurance-complete',
          addressMismatch: sessionStorage.getItem('addressMismatch') === 'true'
        })
      });
      
      if (routerResponse.ok) {
        const routerData = await routerResponse.json();
        const correctStep = routerData.nextStep;
        
        // Map router step names to URL step names
        const routerToUrl = {
          'idenfy': 'document-upload',
          'full-idenfy': 'document-upload',
          'selective-idenfy': 'document-upload',
          'poa-instructions': 'poa-instructions',
          'poa1': 'poa1',
          'poa2': 'poa2',
          'dvla-check': 'dvla-check',
          'passport-upload': 'passport-upload',
          'signature': 'signature'
        };
        
        const mappedStep = routerToUrl[correctStep] || correctStep;
        
        // Special case: processing-hub — only redirect if beyond Idenfy
        if (requestedStep === 'processing-hub') {
          // Back from iDenfy before the result has landed (the usual case —
          // the webhook takes ~30s), so the router still points at the check
          // they've just done. For a PASSPORT check that's 'passport-upload':
          // without this, they were bounced back to the passport page and
          // tended to start a second check (found Oct 2026).
          const backFromPassport = new URLSearchParams(window.location.search).get('sessionType') === 'passport_only';
          if (['idenfy', 'full-idenfy', 'selective-idenfy'].includes(correctStep) ||
              (backFromPassport && correctStep === 'passport-upload')) {
            console.log('🔄 Processing-hub: Idenfy still pending — showing polling UI');
            handleStepChange('processing-hub');
            return;
          }
          console.log(`🔀 Processing-hub skip: Idenfy complete → ${mappedStep}`);
          const mismatch = sessionStorage.getItem('addressMismatch') === 'true';
          window.location.href = `/?step=${mappedStep}&email=${encodeURIComponent(emailParam)}&job=${jobParam}${mismatch ? '&addressMismatch=true' : ''}&routed=1`;
          return;
        }

        // Steps that are valid progressions from the router's recommendation
        // e.g., poa-instructions → poa1 or poa2 is the intended flow
        const validProgressions = {
          'poa-instructions': ['poa1', 'poa2'],
        };

        // Normal case: redirect if step doesn't match (unless it's a valid progression)
        if (mappedStep !== requestedStep && !validProgressions[mappedStep]?.includes(requestedStep)) {
          console.log(`🔀 Smart redirect: ${requestedStep} → ${mappedStep} (${routerData.reason})`);
          const mismatch = sessionStorage.getItem('addressMismatch') === 'true';
          window.location.href = `/?step=${mappedStep}&email=${encodeURIComponent(emailParam)}&job=${jobParam}${mismatch ? '&addressMismatch=true' : ''}&routed=1`;
          return;
        }
        
        console.log(`✅ Smart route confirmed: ${requestedStep}`);
      } else {
        console.warn('⚠️ Smart route check failed — proceeding with requested step');
      }
    } catch (err) {
      console.error('⚠️ Smart route error:', err.message);
    }
    
    // Router agrees OR check failed (fail-open) — render requested page
    handleStepChange(requestedStep);
  };

  // Extract job ID from URL on load - v3.6 UPDATED with poa-instructions and addressMismatch handling
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const jobParam = urlParams.get('job');
    const emailParam = urlParams.get('email');
    const stepParam = urlParams.get('step');
    const mismatchParam = urlParams.get('addressMismatch'); // v3.6: Get mismatch flag from URL
    const routedParam = urlParams.get('routed'); // v3.8: Skip smartRoute when router already validated

    // 🧹 CLEANUP: Minimal URL logging
    console.log('📍 Loading verification:', { step: stepParam, hasEmail: !!emailParam, hasJob: !!jobParam, addressMismatch: mismatchParam, routed: routedParam });
    
    // v3.6: Set address mismatch state from URL parameter
    if (mismatchParam === 'true') {
      setAddressMismatch(true);
      sessionStorage.setItem('addressMismatch', 'true');
    } else if (mismatchParam === 'false') {
      setAddressMismatch(false);
      sessionStorage.setItem('addressMismatch', 'false');
    } else {
      // Try to restore from session storage (in case of page refresh mid-flow)
      const storedMismatch = sessionStorage.getItem('addressMismatch');
      if (storedMismatch === 'true') {
        setAddressMismatch(true);
      }
    }
    
    // Handle direct navigation to specific steps
    if (stepParam === 'processing-hub' && emailParam) {
      const email = decodeURIComponent(emailParam);

      // 🔒 v3.9: Validate session before allowing access — processing-hub was
      // the ONE protected step that skipped this. Without it, a driver whose
      // session token didn't survive the Idenfy round-trip landed on a
      // ProcessingHub that polled driver-status with an empty token, 401'd
      // 20 times, and dead-ended (the "spinning wheel of death").
      // Progress is safe: the Idenfy webhook writes server-side, so after
      // re-verifying their email the router resumes at the right step.
      if (!validateSession(email)) {
        sessionStorage.clear();
        setDriverEmail(email);
        if (jobParam) setJobId(jobParam);
        setError('Your verification session has expired. Please re-enter your email to continue — your progress has been saved.');
        setCurrentStep('email-entry');
        return;
      }

      setDriverEmail(email);
      if (jobParam) setJobId(jobParam);
      smartRoute('processing-hub', email, jobParam);
      return;
    }
    
    // v3.6: Handle POA Instructions step (new gateway)
    if (stepParam === 'poa-instructions' && emailParam) {
      const email = decodeURIComponent(emailParam);
      
      // 🔒 SECURITY: Validate session before allowing access
      if (!validateSession(email)) {
        sessionStorage.clear();
        setDriverEmail(email);
        if (jobParam) setJobId(jobParam);
        setError('Your session has expired. Please enter your email again to continue.');
        setCurrentStep('email-entry');
        return;
      }
      
      setDriverEmail(email);
      if (jobParam) setJobId(jobParam);
      
      // Fetch fresh driver data for POA instructions page (need licence address)
      refreshDriverStatus(email);
      
      // v3.8: Skip smartRoute if we arrived here via a router-validated redirect
      if (routedParam === '1') {
        console.log('✅ Skipping smartRoute — router already validated poa-instructions');
        handleStepChange('poa-instructions');
      } else {
        smartRoute('poa-instructions', email, jobParam);
      }
      return;
    }

    // v3.6: Handle POA1 step - now includes addressMismatch parameter
    if (stepParam === 'poa1' && emailParam) {
      const email = decodeURIComponent(emailParam);

      // 🔒 SECURITY: Validate session before allowing access
      if (!validateSession(email)) {
        sessionStorage.clear();
        // v3.5: Preserve email and jobId from URL for re-authentication
        setDriverEmail(email);
        if (jobParam) setJobId(jobParam);
        setError('Your session has expired. Please enter your email again to continue.');
        setCurrentStep('email-entry');
        return;
      }

      setDriverEmail(email);
      if (jobParam) setJobId(jobParam);

      // Fetch fresh driver data for POA1 page
      refreshDriverStatus(email);

      // v3.8: Skip smartRoute if we arrived here via a router-validated redirect
      if (routedParam === '1') {
        console.log('✅ Skipping smartRoute — router already validated poa1');
        handleStepChange('poa1');
      } else {
        smartRoute('poa1', email, jobParam);
      }
      return;
    }

    // v3.6: Handle POA2 step - now includes addressMismatch parameter
    if (stepParam === 'poa2' && emailParam) {
      const email = decodeURIComponent(emailParam);

      // 🔒 SECURITY: Validate session before allowing access
      if (!validateSession(email)) {
        sessionStorage.clear();
        // v3.5: Preserve email and jobId from URL for re-authentication
        setDriverEmail(email);
        if (jobParam) setJobId(jobParam);
        setError('Your session has expired. Please enter your email again to continue.');
        setCurrentStep('email-entry');
        return;
      }

      setDriverEmail(email);
      if (jobParam) setJobId(jobParam);

      // Try to restore poa1Data from sessionStorage (new driver flow)
      const storedPoa1Data = sessionStorage.getItem('poa1Data');
      if (storedPoa1Data) {
        try {
          setPoa1Data(JSON.parse(storedPoa1Data));
        } catch (e) {
          console.error('❌ Error parsing stored POA1 data:', e.message);
        }
      }

      // Also fetch fresh driver data (has poa1Provider for returning drivers)
      refreshDriverStatus(email);

      // v3.8: Skip smartRoute if we arrived here via a router-validated redirect
      if (routedParam === '1') {
        console.log('✅ Skipping smartRoute — router already validated poa2');
        handleStepChange('poa2');
      } else {
        smartRoute('poa2', email, jobParam);
      }
      return;
    }
    
    // v3.0: Handle DVLA check step (new name from router).
    // 'dvla-processing' is the old name — an old link still lands on the current page.
    if ((stepParam === 'dvla-check' || stepParam === 'dvla-processing') && emailParam) {
      const email = decodeURIComponent(emailParam);

      // 🔒 SECURITY: Validate session before allowing access
      if (!validateSession(email)) {
        sessionStorage.clear();
        // v3.5: Preserve email and jobId from URL for re-authentication
        setDriverEmail(email);
        if (jobParam) setJobId(jobParam);
        setError('Your session has expired. Please enter your email again to continue.');
        setCurrentStep('email-entry');
        return;
      }

      setDriverEmail(email);
      if (jobParam) setJobId(jobParam);
      refreshDriverStatus(email);
      // v3.8: Skip smartRoute if we arrived here via a router-validated redirect
      if (routedParam === '1') {
        console.log('✅ Skipping smartRoute — router already validated dvla-check');
        handleStepChange('dvla-check');
      } else {
        smartRoute('dvla-check', email, jobParam);
      }
      return;
    }
    
    if (stepParam === 'passport-upload' && emailParam) {
      const email = decodeURIComponent(emailParam);
      
      // 🔒 SECURITY: Validate session before allowing access
      if (!validateSession(email)) {
        sessionStorage.clear();
        // v3.5: Preserve email and jobId from URL for re-authentication
        setDriverEmail(email);
        if (jobParam) setJobId(jobParam);
        setError('Your session has expired. Please enter your email again to continue.');
        setCurrentStep('email-entry');
        return;
      }
      
      setDriverEmail(email);
      if (jobParam) setJobId(jobParam);
      // v3.8: Skip smartRoute if we arrived here via a router-validated redirect
      if (routedParam === '1') {
        console.log('✅ Skipping smartRoute — router already validated passport-upload');
        handleStepChange('passport-upload');
      } else {
        smartRoute('passport-upload', email, jobParam);
      }
      return;
    }

    // Photo ID needs a human at Ooosh. ProcessingHub (both maps), smartRoute
    // and the POA pages all navigate to ?step=manual-review, but until this
    // handler existed that URL fell through to "normal job flow" below and
    // landed on email entry — and after re-verifying, the driver was sent
    // back into iDenfy. The loop the screen was built to break was still open.
    //
    // Always re-ask the router here (ignore routed=1): the screen is terminal,
    // so a reload is the driver's only way to find out that staff have
    // accepted their ID and moved them on.
    if (stepParam === 'manual-review' && emailParam) {
      const email = decodeURIComponent(emailParam);

      // 🔒 SECURITY: Validate session before allowing access
      if (!validateSession(email)) {
        sessionStorage.clear();
        setDriverEmail(email);
        if (jobParam) setJobId(jobParam);
        setError('Your session has expired. Please enter your email again to continue.');
        setCurrentStep('email-entry');
        return;
      }

      setDriverEmail(email);
      if (jobParam) setJobId(jobParam);
      smartRoute('manual-review', email, jobParam);
      return;
    }

   if (stepParam === 'signature' && emailParam) {
      const email = decodeURIComponent(emailParam);

      // 🔒 SECURITY: Validate session before allowing access
      if (!validateSession(email)) {
        sessionStorage.clear();
        // v3.5: Preserve email and jobId from URL for re-authentication
        setDriverEmail(email);
        if (jobParam) setJobId(jobParam);
        setError('Your session has expired. Please enter your email again to continue.');
        setCurrentStep('email-entry');
        return;
      }

      console.log('✅ Valid session - signature page access granted');
      setDriverEmail(email);
      if (jobParam) setJobId(jobParam);
      // v3.8: Skip smartRoute if we arrived here via a router-validated redirect
      if (routedParam === '1') {
        console.log('✅ Skipping smartRoute — router already validated signature');
        handleStepChange('signature');
      } else {
        smartRoute('signature', email, jobParam);
      }
      return;
    }
    
    // Handle normal job flow
    if (jobParam) {
      setJobId(jobParam);
      validateJobAndFetchDetails(jobParam);
    } else {
      setError('Invalid verification link. Please check your email for the correct link.');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const validateJobAndFetchDetails = async (jobIdParam) => {
    setLoading(true);
    try {
      const response = await fetch(`/.netlify/functions/validate-job?jobId=${jobIdParam}`);
      
      if (!response.ok) {
        throw new Error('Failed to validate job');
      }
      
      const result = await response.json();
      
      if (!result.valid) {
        setError(result.message || 'This hire is no longer available for driver verification.');
        return;
      }
      
      setJobDetails(result.job);
      handleStepChange('start');
      setError('');
    } catch (err) {
      console.error('❌ Job validation error:', err.message);
      setError('Failed to validate job. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  const sendVerificationEmail = async () => {
    if (!driverEmail || !driverEmail.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      // 🧹 CLEANUP: Removed detailed logging
      console.log('📧 Sending verification code');
      
      const response = await fetch('/.netlify/functions/send-verification-code', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ 
          email: driverEmail, 
          jobId: jobId 
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }
      
      handleStepChange('email-verification');
      
      // 🔒 SECURITY: Removed test mode logging
      
    } catch (err) {
      console.error('❌ Send verification error:', err.message);
      setError(`Failed to send verification email: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // v3.4: Check if hire form already exists for this job + email combo
  // v3.6: Retry once on transient errors (network / 5xx / 429) before falling
  // open, so a single blip doesn't skip the "already completed" screen.
  // Data integrity is backstopped server-side by OP's (driver_id, job_id) dedup.
  const checkHireFormExists = async (email, jobNumber) => {
    console.log('🔍 Checking if hire form already exists for job:', jobNumber);

    const attemptCheck = async () => {
      const response = await fetch('/.netlify/functions/monday-integration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'check-hire-form-exists',
          email: email,
          jobNumber: jobNumber
        })
      });
      if (!response.ok) {
        const err = new Error(`HTTP ${response.status}`);
        err.status = response.status;
        err.transient = response.status >= 500 || response.status === 429;
        throw err;
      }
      return response.json();
    };

    for (let attempt = 1; attempt <= 2; attempt++) {
      try {
        const data = await attemptCheck();
        if (data.exists) {
          console.log('✅ Hire form already exists for this job');
          return { exists: true, assignment: data.assignment };
        }
        return { exists: false };
      } catch (err) {
        // Network errors have no err.status; transient HTTP errors set err.transient
        const isTransient = err.transient || !err.status;
        if (attempt === 1 && isTransient) {
          console.warn(`⚠️ Hire form check attempt 1 failed (${err.message}), retrying in 800ms`);
          await new Promise(r => setTimeout(r, 800));
          continue;
        }
        console.error('❌ Error checking hire form existence:', err.message);
        // Persistent error — allow the user to continue (fail open for UX)
        return { exists: false };
      }
    }
    return { exists: false };
  };

  const verifyEmailCode = async () => {
    if (!verificationCode || verificationCode.length < 6) {
      setError('Please enter the 6-digit code from your email');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const response = await fetch('/.netlify/functions/verify-code', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ 
          email: driverEmail, 
          code: verificationCode,
          jobId: jobId 
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Verification failed');
      }

      if (data.success && data.verified) {
        console.log('✅ Email verified');
        
        // 🔒 v3.5 SECURITY: Store server-provided cryptographic session token
        // This token is validated by the backend on every sensitive API call
        if (data.sessionToken) {
          sessionStorage.setItem('verificationSessionToken', data.sessionToken);
        }
        sessionStorage.setItem('sessionDriverEmail', driverEmail);
        sessionStorage.setItem('sessionStartTime', Date.now().toString());
        console.log('🔒 Secure session created - 40 minute timeout started');
        
        // v3.4: Check if hire form already exists BEFORE continuing
        const hireCheck = await checkHireFormExists(driverEmail, jobId);
        
        if (hireCheck.exists) {
          console.log('🎉 Driver already completed hire form for this job');
          setAlreadyCompletedHireInfo({
            jobNumber: jobId,
            assignment: hireCheck.assignment
          });
          handleStepChange('already-completed');
          return;
        }
        
        // No existing hire form - continue normal flow
        await checkDriverStatusEarly();
      } else {
        throw new Error(data.error || 'Invalid verification code');
      }
      
  } catch (err) {
  console.error('❌ Verify code error:', err.message);
  setVerificationCode('');  // Clear the input
  setError('That code was incorrect. Please click below to request a new one.');

    } finally {
      setLoading(false);
    }
  };

  // v3.5 UPDATED: Now includes session token for authentication
  const checkDriverStatusEarly = async () => {
    try {
      const response = await fetch(`/.netlify/functions/driver-status?email=${encodeURIComponent(driverEmail)}`, {
        headers: {
          'X-Session-Token': getSessionToken()
        }
      });
      
      if (response.ok) {
        const driverData = await response.json();
        console.log('✅ Driver status retrieved');
        setDriverStatus(driverData);
        
        // Pre-populate phone if we have it
        if (driverData.phoneNumber) {
          setPhoneNumber(driverData.phoneNumber);
        }
        if (driverData.phoneCountry) {
          setCountryCode(driverData.phoneCountry);
        }
        
        // Check document validity
        if (driverData.status === 'verified' && driverData.documents) {
          const licenceValid = driverData.documents.licence?.valid;
          const poa1Valid = driverData.documents.poa1?.valid;
          const poa2Valid = driverData.documents.poa2?.valid;
          const dvlaValid = driverData.documents.dvlaCheck?.valid;
          
          if (licenceValid && poa1Valid && poa2Valid && dvlaValid) {
            if (driverData.insuranceData && !needsInsuranceQuestionnaire()) {
              handleStepChange('document-upload');
              return;
            }
          }
        }
        
        // A returning driver who still has something to do sees where they're
        // up to first. Drivers with everything in date skip it — they get
        // WelcomeBackPage after their answers instead.
        const docs = driverData.documents || {};
        const everythingInDate = docs.license?.valid && docs.poa1?.valid && docs.poa2?.valid &&
          (docs.dvlaCheck?.valid || docs.passportCheck?.valid);
        if (driverData.status && driverData.status !== 'new' && !everythingInDate) {
          handleStepChange('resume');
          return;
        }
        handleStepChange('contact-details');
      } else {
        setDriverStatus({ status: 'new', email: driverEmail });
        handleStepChange('contact-details');
      }
      
    } catch (err) {
      console.error('❌ Error checking driver status:', err.message);
      setDriverStatus({ status: 'new', email: driverEmail });
      handleStepChange('contact-details');
    }
  };

  const saveContactDetails = async () => {
    if (!phoneNumber || phoneNumber.length < 7) {
      setError('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const response = await fetch('/.netlify/functions/monday-integration', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'create-driver-board-a',
          email: driverEmail,
          driverData: {
            phoneNumber: phoneNumber,
            phoneCountry: countryCode
          }
        })
      });

      if (response.ok) {
        console.log('✅ Contact details saved');
      } else {
        console.error('⚠️ Failed to save contact details - continuing');
      }
      
      handleStepChange('insurance-questionnaire');
      
    } catch (err) {
      console.error('❌ Error saving contact details:', err.message);
      handleStepChange('insurance-questionnaire');
    } finally {
      setLoading(false);
    }
  };

  const needsInsuranceQuestionnaire = () => {
    return true; // Always require for new hires
  };

      
  // v3.6 UPDATED: Router now routes to poa-instructions instead of poa1/poa2 directly
  const handleInsuranceComplete = async (insuranceFormData) => {
    console.log('✅ Insurance questionnaire completed');
    setLoading(true);

    try {
      // Build referral reasons from questionnaire answers.
      //
      // NOT every yes refers (Oct 2026, Markerstudy guide): "any conviction in
      // the last 5 years" (hasRecentConvictions) and "any accident or claim"
      // (hasAccidents) are RECORDED only — two 3-pointers or one claim are
      // fine. UK points are priced by the DVLA check as before. What refers is
      // the serious-conviction question and "more than one at fault or open".
      const hasFaultClaims = insuranceFormData.hasAccidents === 'yes' && insuranceFormData.hasFaultClaims === 'yes';
      const questionnaireReferralReasons = [];
      if (insuranceFormData.hasDisability === 'yes') questionnaireReferralReasons.push('Driver declared a medical restriction on their driving');
      if (insuranceFormData.hasConvictions === 'yes') questionnaireReferralReasons.push('Driver declared a serious motoring conviction');
      if (insuranceFormData.hasProsecution === 'yes') questionnaireReferralReasons.push('Driver declared a pending prosecution');
      if (hasFaultClaims) questionnaireReferralReasons.push('Driver declared more than one at-fault or open claim (5 years)');
      if (insuranceFormData.hasInsuranceIssues === 'yes') questionnaireReferralReasons.push('Driver declared insurance issues (refused / special terms)');
      if (insuranceFormData.hasDrivingBan === 'yes') questionnaireReferralReasons.push('Driver declared a ban, disqualification or suspension (5 years)');
      const isQuestionnaireReferral = questionnaireReferralReasons.length > 0;

      // Save insurance data to the backend (OP via monday-integration in OP mode).
      // v3.8: This used to be fire-and-forget — any silent failure (network blip,
      // transient OP 5xx, etc.) left date_passed_test + the questionnaire booleans
      // empty in OP forever. The driver kept going, the autoresponder email
      // rendered "Date passed test: Not set", and we had no telemetry. Now:
      //   - retry once after a short backoff on transient errors
      //   - capture status + body on failure for support diagnosis
      //   - fire a critical telemetry event so it appears in the alert pipeline
      //   - block progression on definitive failure rather than silently
      //     forwarding the driver to a hire form with empty insurance data
      const insurancePayload = {
        action: 'update-driver-board-a',
        email: driverEmail,
        updates: {
          datePassedTest: insuranceFormData.datePassedTest,
          hasDisability: insuranceFormData.hasDisability === 'yes',
          hasConvictions: insuranceFormData.hasConvictions === 'yes',
          hasProsecution: insuranceFormData.hasProsecution === 'yes',
          hasAccidents: insuranceFormData.hasAccidents === 'yes',
          hasInsuranceIssues: insuranceFormData.hasInsuranceIssues === 'yes',
          hasDrivingBan: insuranceFormData.hasDrivingBan === 'yes',
          // Recorded, never refer on their own (OP migration 298).
          hasRecentConvictions: insuranceFormData.hasRecentConvictions === 'yes',
          hasFaultClaims,
          additionalDetails: insuranceFormData.additionalDetails || '',
          // v3.3 CRITICAL: Set insurance status to Referral if any answer that
          // REFERS is Yes (see questionnaireReferralReasons above)
          insuranceStatus: isQuestionnaireReferral ? 'Referral' : 'Approved',
          // OP referral fields — triggers notification on the OP backend
          requiresReferral: isQuestionnaireReferral,
          referralStatus: isQuestionnaireReferral ? 'pending' : null,
          referralReasons: isQuestionnaireReferral ? questionnaireReferralReasons : null
        }
      };

      const isTransientStatus = (s) => s === 429 || s === 408 || (s >= 500 && s < 600);
      const attemptInsuranceWrite = async () => {
        return await fetch('/.netlify/functions/monday-integration', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(insurancePayload)
        });
      };

      let insuranceWriteOk = false;
      let lastStatus = null;
      let lastBody = null;
      let lastErrorMessage = null;
      for (let attempt = 1; attempt <= 2; attempt += 1) {
        try {
          const response = await attemptInsuranceWrite();
          lastStatus = response.status;
          if (response.ok) {
            insuranceWriteOk = true;
            console.log('✅ Insurance data saved', { attempt });
            break;
          }
          // Read body for diagnosis without throwing on parse errors
          try {
            lastBody = await response.text();
          } catch {
            lastBody = '(unreadable response body)';
          }
          console.error(
            `⚠️ Insurance write failed (attempt ${attempt}): HTTP ${lastStatus}`,
            lastBody && lastBody.slice(0, 500)
          );
          if (!isTransientStatus(lastStatus)) break; // 4xx — won't fix itself
        } catch (writeErr) {
          lastErrorMessage = writeErr && writeErr.message;
          console.error(`⚠️ Insurance write threw (attempt ${attempt}):`, lastErrorMessage);
        }
        if (attempt === 1) {
          await new Promise((resolve) => setTimeout(resolve, 1500));
        }
      }

      if (!insuranceWriteOk) {
        // Fire-and-forget telemetry to surface this in the existing alert pipeline
        try {
          await fetch('/.netlify/functions/log-client-error', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              severity: 'critical',
              source: 'insurance-questionnaire-submit',
              email: driverEmail,
              jobId,
              message: lastErrorMessage || `HTTP ${lastStatus}`,
              details: {
                status: lastStatus,
                body: lastBody && lastBody.slice(0, 1000),
                hasDatePassedTest: !!insuranceFormData.datePassedTest
              }
            })
          });
        } catch (telemetryErr) {
          console.error('Failed to record insurance write failure:', telemetryErr);
        }
        setLoading(false);
        const saveError = new Error(
          'We couldn\'t save your insurance answers (this includes the date you passed your test). ' +
          'Please check your connection and try again — if the problem persists, contact info@oooshtours.co.uk.'
        );
        // Marked so the catch below re-throws it to the questionnaire, which
        // shows the message. Without the mark the catch swallowed it and sent
        // the driver on to the licence check with nothing saved.
        saveError.isInsuranceSaveFailure = true;
        throw saveError;
      }
      
      
      console.log('🚀 Calling centralized router');
      
      const routerResponse = await fetch('/.netlify/functions/get-next-step', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: driverEmail,
          currentStep: 'insurance-complete'
        })
      });
      
      if (routerResponse.ok) {
        const routerData = await routerResponse.json();
        console.log('✅ Router response:', routerData.nextStep);
        
        const nextStep = routerData.nextStep;
        
        // v3.6 UPDATED: Map router steps to App.js steps
        switch(nextStep) {
          case 'idenfy':
          case 'full-idenfy':
          case 'selective-idenfy':
            handleStepChange('document-upload');
            break;
          case 'poa-instructions':
            // v3.6: New gateway step
            window.location.href = `/?step=poa-instructions&email=${encodeURIComponent(driverEmail)}&job=${jobId}&routed=1`;
            break;
          case 'poa1':
            // v3.6: Direct to POA1 (for returning drivers who don't need instructions)
            window.location.href = `/?step=poa1&email=${encodeURIComponent(driverEmail)}&job=${jobId}&routed=1`;
            break;
          case 'poa2':
            window.location.href = `/?step=poa2&email=${encodeURIComponent(driverEmail)}&job=${jobId}&routed=1`;
            break;
          case 'dvla-check':
            window.location.href = `/?step=dvla-check&email=${encodeURIComponent(driverEmail)}&job=${jobId}&routed=1`;
            break;
          case 'passport-upload':
            window.location.href = `/?step=passport-upload&email=${encodeURIComponent(driverEmail)}&job=${jobId}&routed=1`;
            break;
          case 'signature':
             handleStepChange('welcome-back');
            break;
          case 'manual-review':
            // Photo ID is with a human at Ooosh — the default below would
            // send them straight back into iDenfy.
            handleStepChange('manual-review');
            break;
          default:
            handleStepChange('document-upload');
        }
      } else {
        console.error('❌ Router call failed, using fallback');
        handleStepChange('document-upload');
      }
        
    } catch (err) {
      // A failed save must stop the driver on the questionnaire, which shows
      // this message (InsuranceQuestionnaire.handleSubmit).
      if (err && err.isInsuranceSaveFailure) throw err;
      console.error('❌ Error in insurance completion:', err.message);
      handleStepChange('document-upload');
    } finally {
      setLoading(false);
    }
  };

  const startVerification = () => {
    generateIdenfyToken();
  };

  const startAgain = () => {
    // 🔒 SECURITY: Clear session on restart
    sessionStorage.clear();
    
    setDriverEmail('');
    setPhoneNumber('');
    setCountryCode('+44');
    setVerificationCode('');
    setError('');
    setLoading(false);
    setPoa1Data(null);
    setAlreadyCompletedHireInfo(null);
    setAddressMismatch(false); // v3.6: Clear mismatch flag
    handleStepChange('email-entry');
  };

  const generateIdenfyToken = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Determine verification type
      let verificationType = 'full';
      const docs = driverStatus?.documents;
      
      if (docs) {
        const licenseValid = docs.license?.valid;
        const poa1Valid = docs.poa1?.valid;
        const poa2Valid = docs.poa2?.valid;
        
        if (!licenseValid && poa1Valid && poa2Valid) {
          verificationType = 'license';
        } else if (licenseValid && !poa1Valid && poa2Valid) {
          verificationType = 'poa1';
        } else if (licenseValid && poa1Valid && !poa2Valid) {
          verificationType = 'poa2';
        } else if (licenseValid && !poa1Valid && !poa2Valid) {
          verificationType = 'poa_both';
        } else {
          verificationType = 'full';
        }
      }
      
      const isUKDriver = driverStatus?.licenseIssuedBy === 'UK' || true;
      
      const response = await fetch('/.netlify/functions/create-idenfy-session', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          email: driverEmail,
          jobId: jobId,
          driverName: driverStatus?.name,
          verificationType: verificationType,
          isUKDriver: isUKDriver,
          // v3.9: Idenfy return URLs must come back to THIS origin —
          // sessionStorage (session token) is per-origin, and the app is
          // served on both hireforms.oooshtours.co.uk and the netlify domain.
          origin: window.location.origin
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }
      
      // 🔒 SECURITY: Removed mock mode handling
      if (data.sessionToken) {
        console.log('✅ Redirecting to Idenfy verification');
        window.location.href = data.redirectUrl || `https://ui.idenfy.com/session?authToken=${data.sessionToken}`;
      } else {
        throw new Error('No session token received from Idenfy');
      }
      
    } catch (err) {
      console.error('❌ Idenfy error:', err.message);
      setError(`Failed to start document verification: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

 
  const generateQRCode = () => {
    const currentUrl = window.location.href;
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(currentUrl)}`;
    return qrUrl;
  };

   const handleCountryChange = (e) => {
    setCountryCode(e.target.value);
  };

  // v3.2: Helper to derive license ending from license number
  const getLicenseEnding = () => {
    // Try explicit field first, then derive from license number
    if (driverStatus?.licenseEnding) {
      return driverStatus.licenseEnding;
    }
    if (driverStatus?.licenseNumber && driverStatus.licenseNumber.length >= 8) {
      return driverStatus.licenseNumber.slice(-8);
    }
    return null;
  };

  // ==========================================================================
  // Screens — Oct 2026 redesign. Building blocks: src/ui/HireFormUI.js.
  // These are plain render FUNCTIONS called as renderX(), not components, so
  // they don't fall foul of "never declare a component inside a component".
  // ==========================================================================

  const jobNumberShown = jobDetails?.jobNumber || jobId;
  const hireRange = formatHireRange(jobDetails?.startDate, jobDetails?.endDate);

  const renderErrorNote = () => (error ? <Note tone="error">{error}</Note> : null);

  // Loading, or the job in the link isn't valid for a hire form
  const renderLanding = () => (
    <PlainShell>
      {!loading && error ? (
        <Card>
          <div className="hf-header">
            <Logo />
            <h1 className="hf-h2">We couldn't find this hire</h1>
          </div>
          <p className="hf-intro">This may be because:</p>
          <ul className="hf-bullets">
            <li>the hire has ended or been cancelled</li>
            <li>the link has expired</li>
            <li>the job number in the link isn't right</li>
          </ul>
          <Why lead="Need help?">
            Call us on <a className="hf-link" href={HELP_PHONE_TEL}>{HELP_PHONE_DISPLAY}</a> or email{' '}
            <a className="hf-link" href={`mailto:${HELP_EMAIL}`}>{HELP_EMAIL}</a>.
          </Why>
        </Card>
      ) : (
        <Card>
          <div className="hf-center">
            <Spinner />
            <p className="hf-small" style={{ marginTop: 12 }}>Loading your hire form…</p>
          </div>
        </Card>
      )}
    </PlainShell>
  );

  // Start: what the form involves and what to have ready, before the email.
  // Kept as its own page on purpose (jon, Oct 2026): a driver who reads the
  // "have these ready" list first is less likely to stop half-way to go and
  // find a bill or their DVLA summary.
  const renderStart = () => (
    <PlainShell>
      <div className="hf-header">
        <Logo large />
        <h1 className="hf-h1">Driver hire form</h1>
      </div>
      <p className="hf-job-line">
        Job #{jobNumberShown}{hireRange && <span className="hf-job-dates"> · {hireRange}</span>}
      </p>

      <p className="hf-lead">
        Everyone who'll drive on this hire fills this in. It takes about <strong>15 minutes</strong>.
        Your progress is saved as you go, so you can stop and come back.
      </p>
      <p className="hf-body">
        <strong>Driven with us before?</strong> We'll fill in what we already have. You'll just need to
        check it's still right for this hire.
      </p>

      <Card className="hf-card-tight">
        <div className="hf-steps">
          <StepRow marker="1" title="About you" desc="Your email, phone and a few insurance questions" />
          <StepRow marker="2" title="Your licence" desc="Photos of your licence and a quick selfie" />
          <StepRow marker="3" title="Your address" desc="Two recent letters or bills" />
          <StepRow marker="4" title="Final checks & sign" desc="A licence record check (UK licence) or a passport check, then sign" />
        </div>
      </Card>

      <Card className="hf-card-tight">
        <div className="hf-inset">
          <p className="hf-small hf-strong" style={{ marginBottom: 4 }}>Every driver must be</p>
          <ul className="hf-bullets">
            <li>aged 23 to 75</li>
            <li>a full licence holder for at least 2 years</li>
          </ul>
        </div>
        <div className="hf-inset">
          <p className="hf-small hf-strong" style={{ marginBottom: 4 }}>Please have these ready</p>
          <ul className="hf-bullets">
            <li>Your photocard driving licence</li>
            <li>A phone with a camera, for a quick selfie</li>
            <li>Two letters or bills with your address on, from the last 90 days and from two different places (insurance documents can't be used)</li>
            <li>
              <strong>UK licence:</strong> a copy of your DVLA driving record.{' '}
              <a className="hf-link" href="https://www.gov.uk/view-driving-licence" target="_blank" rel="noopener noreferrer">Get it free on GOV.UK</a>
              {' '}(<a className="hf-link" href="https://www.oooshtours.co.uk/how-to-get-a-dvla-check-code" target="_blank" rel="noopener noreferrer">how</a>)
            </li>
            <li>
              <strong>Northern Ireland licence:</strong> a DVA check code.{' '}
              <a className="hf-link" href="https://www.nidirect.gov.uk/services/view-or-share-your-ni-driving-licence-information" target="_blank" rel="noopener noreferrer">Get one on nidirect</a>
            </li>
            <li><strong>Licence from outside the UK:</strong> your passport</li>
          </ul>
        </div>
      </Card>

      {!isMobile && (
        <Card className="hf-card-tight">
          <div className="hf-row" style={{ alignItems: 'center', gap: 16 }}>
            <img src={generateQRCode()} alt="QR code to open this form on your phone" width="112" height="112" style={{ borderRadius: 8, flexShrink: 0 }} />
            <div>
              <p className="hf-body hf-strong">Best on your phone</p>
              <p className="hf-small">Scan this to carry on on your phone. You'll need its camera for the selfie.</p>
            </div>
          </div>
        </Card>
      )}

      <PrimaryButton onClick={() => handleStepChange('email-entry')}>Start</PrimaryButton>

      <p className="hf-small hf-center">
        Our insurer asks us to check every driver before they take one of our vehicles.{' '}
        <a className="hf-link" href={TERMS_URL} target="_blank" rel="noopener noreferrer">Read our hire terms</a>
      </p>
    </PlainShell>
  );

  const renderEmailEntry = () => (
    <StepShell jobId={jobNumberShown} phase={PHASE.YOU} showSaved={false}>
      <Card>
        <div>
          <h2 className="hf-h2">First, confirm your email</h2>
          <p className="hf-intro" style={{ marginTop: 6 }}>We'll email you a 6-digit code to check it's you.</p>
        </div>
        <label className="hf-field" htmlFor="email">
          <span className="hf-label">Email address</span>
          <input
            type="email"
            id="email"
            className="hf-input"
            value={driverEmail}
            onChange={(e) => setDriverEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            inputMode="email"
            disabled={loading}
          />
        </label>
        {renderErrorNote()}
        <PrimaryButton onClick={sendVerificationEmail} disabled={!driverEmail} busy={loading} busyLabel="Sending…">
          Email me a code
        </PrimaryButton>
        <Why lead="Why we ask:">
          your email links this form to your driver record. If you've driven for us before, we'll fill in
          what we already know.
        </Why>
      </Card>
    </StepShell>
  );

  const renderEmailVerification = () => (
    <StepShell jobId={jobNumberShown} phase={PHASE.YOU} showSaved={false}>
      <Card>
        <div>
          <h2 className="hf-h2">Check your email</h2>
          <p className="hf-intro" style={{ marginTop: 6 }}>
            We've sent a code to <strong className="hf-strong" style={{ wordBreak: 'break-word' }}>{driverEmail}</strong>.
            It can take a minute to arrive, so check your spam folder too.
          </p>
        </div>
        <label className="hf-field" htmlFor="code">
          <span className="hf-label">6-digit code</span>
          <input
            type="text"
            id="code"
            className="hf-input hf-otp"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            maxLength="6"
            inputMode="numeric"
            autoComplete="one-time-code"
            disabled={loading}
          />
        </label>
        {renderErrorNote()}
        <PrimaryButton onClick={verifyEmailCode} disabled={verificationCode.length < 6} busy={loading} busyLabel="Checking…">
          Confirm
        </PrimaryButton>
        <div className="hf-row" style={{ justifyContent: 'space-between' }}>
          <button type="button" className="hf-link-btn" onClick={sendVerificationEmail} disabled={loading}>
            Send a new code
          </button>
          <button type="button" className="hf-link-btn" onClick={startAgain} disabled={loading}>
            Use a different email
          </button>
        </div>
      </Card>
    </StepShell>
  );

  // Welcome back, not finished: a returning driver whose documents aren't all
  // in date (all-in-date drivers get WelcomeBackPage after their answers
  // instead). Display only — it changes no routing. "About you" always reads
  // "check your answers": every hire asks them to confirm phone and insurance
  // answers again, so it is never "done" at this point.
  const renderResume = () => {
    const docs = driverStatus?.documents || {};
    const name = firstNameOf(driverStatus?.name);
    const issuedBy = driverStatus?.licenseIssuedBy;
    const uk = isUkIssuer(issuedBy);
    const ni = isNiIssuer(issuedBy);
    const licenceDone = !!docs.license?.valid;
    const addressDone = !!(docs.poa1?.valid && docs.poa2?.valid);
    const passportNeeded = !uk || driverStatus?.addressMismatch === true;
    const recordCheckDone = uk ? !!docs.dvlaCheck?.valid : true;
    const passportDone = passportNeeded ? !!docs.passportCheck?.valid : true;
    let signDesc = 'Just sign for this hire';
    if (!issuedBy) signDesc = 'A licence record check or a passport check, then sign';
    else if (!recordCheckDone && !passportDone) signDesc = `${ni ? 'DVA' : 'DVLA'} check and passport, then sign`;
    else if (!recordCheckDone) signDesc = `${ni ? 'DVA licence check' : 'DVLA record check'}, then sign`;
    else if (!passportDone) signDesc = 'Passport check, then sign';

    const parts = [
      { title: 'About you', state: 'next', aside: 'Check your answers' },
      { title: 'Your licence', state: licenceDone ? 'done' : 'todo' },
      { title: 'Your address', state: addressDone ? 'done' : 'todo' },
      { title: 'Final checks & sign', state: 'todo', desc: signDesc },
    ];

    return (
      <PlainShell showSaved>
        <div className="hf-header">
          <Logo large />
          <h1 className="hf-h1">Welcome back{name ? `, ${name}` : ''}</h1>
        </div>
        <p className="hf-job-line">
          Job #{jobNumberShown}{hireRange && <span className="hf-job-dates"> · {hireRange}</span>}
        </p>
        <Note tone="amber">
          <strong>You're not finished yet.</strong> You can't drive on this hire until all 4 parts are
          done and you've signed.
        </Note>
        <Card className="hf-card-tight">
          <div className="hf-steps">
            {parts.map((p, i) => (
              <StepRow
                key={p.title}
                marker={p.state === 'done' ? '✓' : String(i + 1)}
                markerState={p.state === 'done' ? 'is-done' : p.state === 'next' ? 'is-current' : 'is-todo'}
                title={p.title}
                desc={p.desc}
                aside={p.state === 'done' ? 'Done' : p.aside}
                asideState={p.state === 'done' ? 'is-done' : p.state === 'next' ? 'is-next' : ''}
              />
            ))}
          </div>
          <p className="hf-small">Anything still in date from last time is kept, so you won't be asked for it again.</p>
        </Card>
        <PrimaryButton onClick={() => handleStepChange('contact-details')}>Carry on: check your answers</PrimaryButton>
      </PlainShell>
    );
  };

  // v3.4: Already signed for this job
  const renderAlreadyCompleted = () => (
    <PlainShell>
      <Card>
        <div className="hf-big-tick" aria-hidden="true">✓</div>
        <p className="hf-eyebrow">Already signed</p>
        <h1 className="hf-h2 hf-center">You've already done this one</h1>
        <p className="hf-intro hf-center">
          You've already completed a hire form for job #{alreadyCompletedHireInfo?.jobNumber || jobId}
          {hireRange ? ` (${hireRange})` : ''}. There's nothing else you need to do.
        </p>
        <Why lead="Think this is wrong?">
          Call us on <a className="hf-link" href={HELP_PHONE_TEL}>{HELP_PHONE_DISPLAY}</a> or email{' '}
          <a className="hf-link" href={`mailto:${HELP_EMAIL}`}>{HELP_EMAIL}</a>.
        </Why>
      </Card>
    </PlainShell>
  );

  // The iDenfy hand-off
  const renderDocumentUpload = () => (
    <StepShell jobId={jobNumberShown} phase={PHASE.LICENCE}>
      <Card>
        <div>
          <h2 className="hf-h2">Check your licence</h2>
          <p className="hf-intro" style={{ marginTop: 6 }}>
            You'll take photos of your licence and a short selfie, so we can check the licence is real
            and that it's yours.
          </p>
        </div>
        <div className="hf-steps">
          <StepRow marker="1" title="Front and back of your photocard licence" />
          <StepRow marker="2" title="A few seconds of selfie video" />
        </div>
        <div className="hf-inset">
          <p className="hf-small">
            For a first-time pass: use good light, put the licence on a dark surface, and avoid glare on the photo.
          </p>
        </div>
        {renderErrorNote()}
        <PrimaryButton onClick={startVerification} busy={loading} busyLabel="Opening…">
          Start licence check
        </PrimaryButton>
        <Why lead="What happens next:">
          a secure page from our ID partner, iDenfy, opens. When you finish, you'll come straight back
          here. Please don't close the page in between.
        </Why>
        <div className="hf-row" style={{ justifyContent: 'space-between' }}>
          <button type="button" className="hf-link-btn" onClick={() => handleStepChange('insurance-questionnaire')}>
            Back
          </button>
          <button type="button" className="hf-link-btn" onClick={startAgain}>
            Use a different email
          </button>
        </div>
      </Card>
    </StepShell>
  );

  /**
   * Terminal screen for a driver whose photo ID check needs a human.
   *
   * This is the exit from what used to be an infinite loop. When iDenfy could
   * not match the selfie to the licence photo, the routing engine's fallback
   * always returned 'idenfy' — so the driver was sent to re-verify, failed the
   * same way, and was sent again, forever. Nothing in the router read the
   * "Stuck" flag the webhook had already written. Older drivers whose
   * appearance has moved on from their licence photo hit this repeatedly.
   *
   * Ooosh is alerted the moment the flag is raised, so this genuinely is the
   * end of the driver's involvement until someone looks.
   */
  const renderManualReview = () => (
    <StepShell jobId={jobNumberShown} phase={PHASE.LICENCE} showSaved={false}>
      <Card>
        <p className="hf-eyebrow is-amber" style={{ textAlign: 'left' }}>Nothing more to do for now</p>
        <h2 className="hf-h2">We're checking this by hand</h2>
        <p className="hf-intro">
          Thanks, we've got everything we need from you for now. Our automatic ID check couldn't confirm
          your photo ID by itself, so a member of the Ooosh team needs to look at it. This is common and
          usually nothing to worry about: ID photos can be years old.
        </p>
        <Note tone="accent">
          <strong>You don't need to do anything else right now.</strong> We'll check your documents and be in touch.
        </Note>
        <Why lead="Hire starting soon?">
          Call us on <a className="hf-link" href={HELP_PHONE_TEL}>{HELP_PHONE_DISPLAY}</a> or email{' '}
          <a className="hf-link" href={`mailto:${HELP_EMAIL}`}>{HELP_EMAIL}</a>.
        </Why>
      </Card>
    </StepShell>
  );

  const renderStep = () => {
    switch(currentStep) {
      case 'landing': return renderLanding();
      case 'start': return renderStart();
      case 'email-entry': return renderEmailEntry();
      case 'resume': return renderResume();
      case 'email-verification': return renderEmailVerification();
      case 'already-completed': return renderAlreadyCompleted();
      case 'manual-review': return renderManualReview();
      case 'contact-details': return (
        <ContactDetails
          jobId={jobNumberShown}
          driverStatus={driverStatus}
          driverEmail={driverEmail}
          countryCode={countryCode}
          onCountryChange={handleCountryChange}
          phoneNumber={phoneNumber}
          onPhoneNumberChange={setPhoneNumber}
          error={error}
          loading={loading}
          onContinue={saveContactDetails}
        />
      );
      case 'insurance-questionnaire': return (
        <InsuranceQuestionnaire
          driverStatus={driverStatus}
          driverEmail={driverEmail}
          jobId={jobId}
          phoneNumber={phoneNumber}
          loading={loading}
          onComplete={handleInsuranceComplete}
          onBack={() => handleStepChange('contact-details')}
        />
      );
      case 'document-upload': return renderDocumentUpload();
      case 'processing-hub':
        const urlParams = new URLSearchParams(window.location.search);
        return (
          <ProcessingHub 
            driverEmail={urlParams.get('email') || driverEmail}
            jobId={urlParams.get('job') || jobId}
            sessionType={urlParams.get('sessionType') || 'full'}
          />
        );
      
      // v3.6: POA Instructions gateway - shows licence address and mismatch option
      case 'poa-instructions':
        return (
          <POAInstructionsPage
            jobId={jobId}
            email={driverEmail}
            driverName={driverStatus?.name}
            licenseAddress={driverStatus?.licenseAddress}
            onContinue={async (mismatch) => {
              // Store mismatch flag in state and session storage
              setAddressMismatch(mismatch);
              sessionStorage.setItem('addressMismatch', mismatch ? 'true' : 'false');
              
              console.log(`🏠 Address mismatch declared: ${mismatch}`);

              // ...and on the driver in OP (migration 298). sessionStorage is
              // per-tab, so a driver who came back from the email link after
              // their POAs lost the flag and OP's router sent them past the
              // passport a UK driver with a different address must supply.
              // OP's router now reads the stored value too. Never a gate: if
              // this fails, this tab still carries the flag as before.
              try {
                const saveResponse = await fetch('/.netlify/functions/monday-integration', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    action: 'update-driver-board-a',
                    email: driverEmail,
                    updates: { addressMismatch: mismatch }
                  })
                });
                if (!saveResponse.ok) throw new Error(`HTTP ${saveResponse.status}`);
              } catch (saveErr) {
                console.error('⚠️ Could not save address choice to OP:', saveErr.message);
                reportError({
                  page: 'POAInstructionsPage',
                  error: `Address mismatch choice not saved (${mismatch}): ${saveErr.message}`,
                  severity: 'warning',
                  email: driverEmail
                });
              }
              
              // v3.8: Smart POA routing - check which POA step is actually needed
              // If POA1 is still valid, skip straight to POA2
              const poa1StillValid = driverStatus?.documents?.poa1?.valid || 
                (driverStatus?.poa1ValidUntil && new Date(driverStatus.poa1ValidUntil) > new Date());
              const targetStep = poa1StillValid ? 'poa2' : 'poa1';
              console.log(`📋 POA routing: POA1 valid=${poa1StillValid} → ${targetStep}`);
              
              window.location.href = `/?step=${targetStep}&email=${encodeURIComponent(driverEmail)}&job=${jobId}&addressMismatch=${mismatch}&routed=1`;
            }}
            
            onBack={() => {
              window.location.href = `/?step=poa-instructions&email=${encodeURIComponent(driverEmail)}&job=${jobId}`;
            }}
          />
        );
      
      // v3.6: POA1 page - now passes addressMismatch
      case 'poa1':
        return (
          <POA1Page 
            jobId={jobId}
            email={driverEmail}
            driverName={driverStatus?.name}
            licenseAddress={driverStatus?.licenseAddress}
            existingPoa2Provider={driverStatus?.documents?.poa2?.valid ? driverStatus?.poa2Provider : null}
            addressMismatch={addressMismatch}  // v3.6: Pass mismatch flag
            onComplete={async (data) => {
              // Store POA1 data for POA2 to reference
              setPoa1Data(data);
              sessionStorage.setItem('poa1Data', JSON.stringify(data));
              
              // Call centralized router to determine next step
              try {
                const routerResponse = await fetch('/.netlify/functions/get-next-step', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    email: driverEmail,
                    currentStep: 'poa1-complete',
                    addressMismatch: addressMismatch  // v3.6: Include in router call
                  })
                });
                
                if (routerResponse.ok) {
                  const routerData = await routerResponse.json();
                  console.log('✅ POA1 router response:', routerData.nextStep);
                  // Preserve mismatch flag in URL
                  window.location.href = `/?step=${routerData.nextStep}&email=${encodeURIComponent(driverEmail)}&job=${jobId}&addressMismatch=${addressMismatch}&routed=1`;
                } else {
                  // Fallback to POA2 if router fails
                  window.location.href = `/?step=poa2&email=${encodeURIComponent(driverEmail)}&job=${jobId}&addressMismatch=${addressMismatch}&routed=1`;
                }
              } catch (err) {
                console.error('❌ Router error:', err);
                window.location.href = `/?step=poa2&email=${encodeURIComponent(driverEmail)}&job=${jobId}&addressMismatch=${addressMismatch}&routed=1`;
              }
            }}
            onBack={() => {
              // Go back to instructions page
              window.location.href = `/?step=poa-instructions&email=${encodeURIComponent(driverEmail)}&job=${jobId}`;
            }}
          />
        );
      
      // v3.6: POA2 page - now passes addressMismatch
      case 'poa2':          
        return (
          <POA2Page 
            jobId={jobId}
            email={driverEmail}
            driverName={driverStatus?.name}
            licenseAddress={driverStatus?.licenseAddress}
            poa1HomeAddress={driverStatus?.homeAddress}
            existingPoa1Provider={poa1Data?.provider || (driverStatus?.documents?.poa1?.valid ? driverStatus?.poa1Provider : null)}
            addressMismatch={addressMismatch}  // v3.6: Pass mismatch flag
            onComplete={async (data) => {
              // Clear stored POA1 data
              sessionStorage.removeItem('poa1Data');
              
              // Call centralized router to determine next step
              try {
                const routerResponse = await fetch('/.netlify/functions/get-next-step', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    email: driverEmail,
                    currentStep: 'poa2-complete',
                    addressMismatch: addressMismatch  // v3.6: Include in router call
                  })
                });
                
                if (routerResponse.ok) {
                  const routerData = await routerResponse.json();
                  console.log('✅ POA2 router response:', routerData.nextStep);
                  // Mismatch flag no longer needed after POA2 - routing decision made
                  window.location.href = `/?step=${routerData.nextStep}&email=${encodeURIComponent(driverEmail)}&job=${jobId}&routed=1`;
                } else {
                  // Fallback based on driver type.
                  // isUkIssuer, NOT `=== 'DVLA'`: a DVA (Northern Ireland)
                  // licence is a UK licence and needs the record check, so the
                  // strict comparison sent NI drivers to a passport they don't
                  // need — disagreeing with OP's router on the one path where
                  // this fallback is all we have.
                  const isUKDriver = isUkIssuer(driverStatus?.licenseIssuedBy);
                  window.location.href = `/?step=${isUKDriver ? 'dvla-check' : 'passport-upload'}&email=${encodeURIComponent(driverEmail)}&job=${jobId}&routed=1`;
                }
              } catch (err) {
                console.error('❌ Router error:', err);
                // isUkIssuer — see the note on the fallback above.
                const isUKDriver = isUkIssuer(driverStatus?.licenseIssuedBy);
                window.location.href = `/?step=${isUKDriver ? 'dvla-check' : 'passport-upload'}&email=${encodeURIComponent(driverEmail)}&job=${jobId}&routed=1`;
              }
            }}
            onBack={() => {
              window.location.href = `/?step=poa-instructions&email=${encodeURIComponent(driverEmail)}&job=${jobId}`;
            }}
          />
        );
      
      // v3.2: DVLA preview page - uses getLicenseEnding() helper for consistent spelling
      //
      // ONE step, TWO pages. OP's router sends every UK driver here, Northern
      // Ireland included — correctly, because NI is the UK and a DVA driver
      // needs the same licence record check. What differs is only how the check
      // can be obtained: a GB driver generates a share code and uploads the
      // summary, while viewdrivingrecord.service.gov.uk holds no NI licences at
      // all, so a DVA driver gives us a nidirect code and a member of staff
      // runs the lookup.
      //
      // Branching HERE rather than adding a router step is deliberate: a new
      // step has to be registered in ProcessingHub's two
      // `stepMapping[nextStep] || 'poa1'` maps and in isValidStepTransition, and
      // missing either silently dumps the driver on a proof-of-address page.
      // Nothing in the routing engine changes.
      case 'dvla-check':
        return isNiIssuer(driverStatus?.licenseIssuedBy) ? (
          <DVACheckPage
            email={driverEmail}
            jobId={jobId}
            driverName={driverStatus?.name}
            licenceNumber={driverStatus?.licenseNumber}
            existingCheckCode={driverStatus?.dvlaCheckCode}
            onBack={() => {
              window.location.href = `/?step=poa2&email=${encodeURIComponent(driverEmail)}&job=${jobId}`;
            }}
          />
        ) : (
          <DVLAPreviewPage 
            jobId={jobId}
            email={driverEmail}
            driverName={driverStatus?.name}
            licenseEnding={getLicenseEnding()}
            onComplete={() => {
              window.location.href = `/?step=signature&email=${encodeURIComponent(driverEmail)}&job=${jobId}&routed=1`;
            }}
            onBack={() => {
              window.location.href = `/?step=poa2&email=${encodeURIComponent(driverEmail)}&job=${jobId}`;
            }}
          />
        );
      
      case 'passport-upload':
        return (
          <PassportUploadPage 
            driverEmail={driverEmail}
            jobId={jobId}
          />
        );
      
// SESSION 97: Welcome back page for returning drivers with all valid docs
case 'welcome-back':
    return (
        <WelcomeBackPage
            jobId={jobId}
            email={driverEmail}
            driverName={driverStatus?.name}
            onContinue={() => {
                // Everything's the same — go straight to signature
                window.location.href = `/?step=signature&email=${encodeURIComponent(driverEmail)}&job=${jobId}&routed=1`;
            }}
            onDetailsChanged={async () => {
                // Dates have been expired in Monday.com by WelcomeBackPage
                // Re-run the router to find the correct next step
                try {
                    const routerResponse = await fetch('/.netlify/functions/get-next-step', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            email: driverEmail,
                            currentStep: 'insurance-complete'
                        })
                    });
                    
                    if (routerResponse.ok) {
                        const routerData = await routerResponse.json();
                        console.log('🔄 Re-routed after details change:', routerData.nextStep);
                        
                        const nextStep = routerData.nextStep;
                        // Use same mapping as handleInsuranceComplete
                        switch(nextStep) {
                            case 'idenfy':
                            case 'full-idenfy':
                            case 'selective-idenfy':
                                handleStepChange('document-upload');
                                break;
                            case 'poa-instructions':
                                window.location.href = `/?step=poa-instructions&email=${encodeURIComponent(driverEmail)}&job=${jobId}&routed=1`;
                                break;
                            case 'poa1':
                                window.location.href = `/?step=poa1&email=${encodeURIComponent(driverEmail)}&job=${jobId}&routed=1`;
                                break;
                            case 'poa2':
                                window.location.href = `/?step=poa2&email=${encodeURIComponent(driverEmail)}&job=${jobId}&routed=1`;
                                break;
                            case 'dvla-check':
                                window.location.href = `/?step=dvla-check&email=${encodeURIComponent(driverEmail)}&job=${jobId}&routed=1`;
                                break;
                            case 'passport-upload':
                                window.location.href = `/?step=passport-upload&email=${encodeURIComponent(driverEmail)}&job=${jobId}&routed=1`;
                                break;
                            case 'manual-review':
                                handleStepChange('manual-review');
                                break;
                            default:
                                handleStepChange('document-upload');
                        }
                    } else {
                        // Without this the page's "Updating..." spinner sat
                        // there forever. Same fallback as the catch below.
                        console.error('❌ Re-routing failed: router returned', routerResponse.status);
                        handleStepChange('document-upload');
                    }
                } catch (err) {
                    console.error('❌ Re-routing error:', err);
                    handleStepChange('document-upload');
                }
            }}
        />
    ); 

      case 'signature': return <SignaturePage driverEmail={driverEmail} jobId={jobId} />;
      default: return renderLanding();
    }
  };

  // Every screen draws its own page frame (StepShell / PlainShell).
  return (
    <ErrorBoundary>
      {renderStep()}
    </ErrorBoundary>
  );
};

export default DriverVerificationApp;
